/**
 * Elastic Sheet Physics Engine
 * 
 * 实现一个全局位移场特效：
 * - 将整个视口看作连续的弹性床单
 * - 鼠标作为局部“压强”源，注入惯性位移并向外传播
 * - 利用 Laplacian 传播公式与阻尼控制数值稳定性
 * - 所有卡片从该全局场中采样位移/梯度
 */

export interface Vec2 {
  x: number;
  y: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

interface FieldPoint {
  height: number;
  velocity: number;
}

export interface DisplacementSample {
  displacement: number;  // z 方向位移
  gradientX: number;     // x 方向梯度（用于计算 rotateY）
  gradientY: number;     // y 方向梯度（用于计算 rotateX）
  normal: Vec3;          // 表面法向量
}

export interface SheetConfig {
  gridWidth: number;
  gridHeight: number;
  cellSize: number;
  stiffness: number;
  damping: number;
  pressureRadius: number;
  pressureStrength: number;
  maxDeflection: number;
}

/**
 * 参数说明
  gridWidth/gridHeight（elasticSheet.ts (lines 34-54)）：决定网格在横纵方向上的采样点数量，值越大网格越细、传力更精细但性能略差；越小则传播更粗略，建议根据视口大小调节。
  cellSize（同上）：一个网格单元覆盖的像素长度，数值越小每个点覆盖越小，渲染越精细但需要更多点；调大则传播更平滑、动效更宽泛。
  stiffness（同上）：决定拉普拉斯传播的强度，值越高相邻点高度差被快速拉平，反应更快速但可能显得僵硬；值低则传播柔和、阻尼感更强。
  damping（同上）：控制速度衰减，接近 1 的值保留更多惯性，动效延续；值偏低时复位更快但容易显得突兀。
  pressureRadius（同上）：鼠标作为局部压强源时作用的范围，半径越大影响越广。
  pressureStrength（同上）：压寻找计的力度，越大一次鼠标动作制造的位移越深，但也更容易发散，要配合 damping。
  maxDeflection（同上）：限制字段高度的最大绝对值，防止数值发散，越大凹陷越剧烈。
  这些参数组合决定“床单”如何响应鼠标：粗细的格子决定力传递的细节，pressure 和 stiffness 决定下压的强度与传播速度，damping 与 maxDeflection 保证动画平滑且有回弹。
 */

const DEFAULT_CONFIG: SheetConfig = {
  gridWidth: 400, // 40
  gridHeight: 260, // 26
  cellSize: 10, // 60
  stiffness: 0.32, // 0.32
  damping: 0.9, // 0.9
  pressureRadius: 2400, // 240
  pressureStrength: 2, // 72
  maxDeflection: 90 // 90
};

export class ElasticSheetEngine {
  private config: SheetConfig;
  private grid: FieldPoint[][];
  private mousePosition: Vec2 | null = null;
  private animationId: number | null = null;
  private lastTime: number = 0;
  private subscribers: Set<() => void> = new Set();
  private _frameCount: number = 0;

  constructor(config: Partial<SheetConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.grid = [];
    this.initializeGrid();
  }

  private initializeGrid(): void {
    const { gridWidth, gridHeight } = this.config;
    this.grid = [];
    for (let j = 0; j < gridHeight; j++) {
      const row: FieldPoint[] = [];
      for (let i = 0; i < gridWidth; i++) {
        row.push({ height: 0, velocity: 0 });
      }
      this.grid.push(row);
    }
  }

  setMousePosition(pos: Vec2 | null): void {
    this.mousePosition = pos;
  }

  private captureHeights(): number[][] {
    return this.grid.map(row => row.map(point => point.height));
  }

  private applyPressure(dt: number): void {
    if (!this.mousePosition) return;
    const { pressureRadius, pressureStrength, cellSize } = this.config;
    const radiusSq = pressureRadius * pressureRadius;

    for (let j = 0; j < this.grid.length; j++) {
      for (let i = 0; i < this.grid[j].length; i++) {
        const px = i * cellSize;
        const py = j * cellSize;
        const dx = px - this.mousePosition.x;
        const dy = py - this.mousePosition.y;
        const distSq = dx * dx + dy * dy;

        if (distSq < radiusSq) {
          const falloff = Math.exp(-distSq / (radiusSq * 0.4));
          const force = -pressureStrength * falloff;
          this.grid[j][i].velocity += force * dt;
        }
      }
    }
  }

  private getSnapshotHeight(i: number, j: number, snapshot: number[][]): number {
    const clampedI = Math.max(0, Math.min(this.config.gridWidth - 1, i));
    const clampedJ = Math.max(0, Math.min(this.config.gridHeight - 1, j));
    return snapshot[clampedJ][clampedI] ?? 0;
  }

  private clampHeight(value: number): number {
    return Math.max(-this.config.maxDeflection, Math.min(this.config.maxDeflection, value));
  }

  private update(deltaTime: number): void {
    const { cellSize, stiffness, damping } = this.config;
    const dt = Math.min(deltaTime, 0.033);

    this.applyPressure(dt);
    const snapshot = this.captureHeights();

    for (let j = 0; j < this.grid.length; j++) {
      for (let i = 0; i < this.grid[j].length; i++) {
        const point = this.grid[j][i];
        const height = snapshot[j][i];

        const laplacian = (
          this.getSnapshotHeight(i - 1, j, snapshot) +
          this.getSnapshotHeight(i + 1, j, snapshot) +
          this.getSnapshotHeight(i, j - 1, snapshot) +
          this.getSnapshotHeight(i, j + 1, snapshot) -
          4 * height
        ) / cellSize;

        const restoreForce = -0.25 * height;
        const hookForce = stiffness * laplacian + restoreForce;
        point.velocity = (point.velocity + hookForce * dt) * damping;
        point.height += point.velocity * dt * 60;
        point.height = this.clampHeight(point.height);
      }
    }

    this._frameCount++;
  }

  sampleDisplacement(worldX: number, worldY: number): DisplacementSample {
    const { cellSize, gridWidth, gridHeight } = this.config;
    const gx = worldX / cellSize;
    const gy = worldY / cellSize;
    const i = Math.floor(gx);
    const j = Math.floor(gy);
    const fx = gx - i;
    const fy = gy - j;
    const i0 = Math.max(0, Math.min(i, gridWidth - 2));
    const j0 = Math.max(0, Math.min(j, gridHeight - 2));
    const i1 = Math.min(gridWidth - 1, i0 + 1);
    const j1 = Math.min(gridHeight - 1, j0 + 1);

    const z00 = this.grid[j0][i0]?.height ?? 0;
    const z10 = this.grid[j0][i1]?.height ?? 0;
    const z01 = this.grid[j1][i0]?.height ?? 0;
    const z11 = this.grid[j1][i1]?.height ?? 0;

    const z0 = z00 * (1 - fx) + z10 * fx;
    const z1 = z01 * (1 - fx) + z11 * fx;
    const displacement = z0 * (1 - fy) + z1 * fy;

    const gradientX = ((z10 - z00) * (1 - fy) + (z11 - z01) * fy) / cellSize;
    const gradientY = ((z01 - z00) * (1 - fx) + (z11 - z10) * fx) / cellSize;
    const normal = this.normalizeVec3({ x: -gradientX, y: -gradientY, z: 1 });

    return { displacement, gradientX, gradientY, normal };
  }

  sampleMultiplePoints(points: Vec2[]): DisplacementSample[] {
    return points.map(p => this.sampleDisplacement(p.x, p.y));
  }

  private normalizeVec3(v: Vec3): Vec3 {
    const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    if (len === 0) return { x: 0, y: 0, z: 1 };
    return { x: v.x / len, y: v.y / len, z: v.z / len };
  }

  start(): void {
    if (this.animationId !== null) return;
    this.lastTime = performance.now();

    const animate = (time: number) => {
      const deltaTime = (time - this.lastTime) / 1000;
      this.lastTime = time;
      this.update(deltaTime);
      this.notifySubscribers();
      this.animationId = requestAnimationFrame(animate);
    };

    this.animationId = requestAnimationFrame(animate);
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  subscribe(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback();
    }
  }

  get frameCount(): number {
    return this._frameCount;
  }

  resize(width: number, height: number): void {
    const { cellSize } = this.config;
    this.config.gridWidth = Math.ceil(width / cellSize) + 2;
    this.config.gridHeight = Math.ceil(height / cellSize) + 2;
    this.initializeGrid();
  }

  reset(): void {
    for (const row of this.grid) {
      for (const point of row) {
        point.height = 0;
        point.velocity = 0;
      }
    }
  }

  destroy(): void {
    this.stop();
    this.subscribers.clear();
  }
}

let globalEngine: ElasticSheetEngine | null = null;

export function getElasticSheetEngine(): ElasticSheetEngine {
  if (!globalEngine) {
    globalEngine = new ElasticSheetEngine();
    globalEngine.start();
  }
  return globalEngine;
}

export function destroyElasticSheetEngine(): void {
  if (globalEngine) {
    globalEngine.destroy();
    globalEngine = null;
  }
}
